import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import II18 from '../translate/II18';
import { Icon, List, ListInput } from 'framework7-react';
import PropTypes from 'prop-types';
import { arrUpdate } from '../helpers/immutable';
import 'react-international-phone/style.css';
import EmailPhoneInput from './EmailPhoneInput';

/**
 * Fields-only credentials body. Host owns CTAs (Weiter / Cancel / Close).
 * Imperative API: isValid(), getMergedUsersData().
 */
const UserCredentialsFields = React.forwardRef(function UserCredentialsFields(
  props,
  ref
) {
  const { usersData, usersDataOk, region, active, onValidityChange } = props;

  const [currentUsersData, setCurrentUsersData] = useState(usersData);
  const prevActiveRef = useRef(active);

  const updateUsersData = (patch, userIndex) =>
    setCurrentUsersData(
      arrUpdate(currentUsersData, userIndex, {
        ...currentUsersData[userIndex],
        ...patch
      })
    );

  const updateUsersNumberOrEmail = (value, userIndex) =>
    setCurrentUsersData(
      arrUpdate(currentUsersData, userIndex, {
        ...currentUsersData[userIndex],
        userEmail: value
      })
    );

  const updateUsersPhone = (value, userIndex) =>
    setCurrentUsersData(
      arrUpdate(currentUsersData, userIndex, {
        ...currentUsersData[userIndex],
        userPhone: value
      })
    );

  useEffect(() => {
    const justActivated = !prevActiveRef.current && active;
    prevActiveRef.current = active;
    if (justActivated || !active) {
      setCurrentUsersData(usersData);
    }
  }, [active, usersData]);

  const isValid =
    Array.isArray(currentUsersData) &&
    !currentUsersData.some(user => !usersDataOk(user));

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [isValid, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      isValid: () => isValid,
      getMergedUsersData: () =>
        (usersData || []).map(u => {
          const updated = currentUsersData.find(_u => _u.id === u.id);
          return updated || u;
        })
    }),
    [isValid, usersData, currentUsersData]
  );

  const userFields = currentUsersData[0]?.userFields || [];
  const manyUsers = currentUsersData.length > 1;

  const getUserNameWidget = (
    id,
    userIndex,
    readOnly,
    label,
    value,
    fieldKey
  ) => (
    <ListInput
      style={{
        listStyle: 'none'
      }}
      id={id}
      className={
        'withEditStartSlot userFields ' + (readOnly ? ' readonly' : '')
      }
      readonly={readOnly}
      outline
      required
      floatingLabel
      label={label}
      type="text"
      placeholder={label}
      clearButton={!readOnly}
      value={value || ''}
      onChange={e => updateUsersData({ [fieldKey]: e.target.value }, userIndex)}
      onInputClear={e => {
        e.preventDefault();
        setTimeout(() => updateUsersData({ [fieldKey]: '' }, userIndex), 50);
      }}
    >
      <Icon color={'gray'} slot={'input'} material={'account_circle'} />
    </ListInput>
  );

  return (
    <div
      className={'tinny-scrollbar'}
      style={{
        background: '#fff',
        minWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxHeight: props.maxHeight || '45vh',
        overflowY: 'auto',
        overflowX: 'clip',
        margin: props.margin != null ? props.margin : '40px 0',
        padding: '10px 0',
        ...props.style
      }}
      data-testid="userDataBlock"
    >
      <List
        noHairlinesMd
        className={'userCredentialsForm'}
        style={{
          width: '100%',
          margin: '0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {currentUsersData.map((user, userIndex) => {
          const userName = user.userName;
          const qesFirstName = user.qesFirstName;
          const qesLastName = user.qesLastName;
          const userNumberOrEmail = user.userEmail;
          const userPhone = user.userPhone;
          const prefix = userIndex > 0 ? '_' + userIndex : '';

          const nameLabel = manyUsers
            ? II18.get('create_case_name')
            : II18.get('create_case_your_name');
          const emailPhoneLabel = manyUsers
            ? II18.get('create_case_email_phone')
            : II18.get('create_case_your_email_phone');

          const nameReadOnly = Boolean(usersData[userIndex]?.userName);
          const qesFirstNameReadOnly = Boolean(
            usersData[userIndex]?.qesFirstName
          );
          const qesLastNameReadOnly = Boolean(
            usersData[userIndex]?.qesLastName
          );
          const emailReadOnly = Boolean(usersData[userIndex]?.userEmail);
          return (
            user.enabled && (
              <div
                key={userIndex}
                className={manyUsers ? 'user-data-block' : ''}
                style={{ width: '100%' }}
              >
                {manyUsers && (
                  <div className="user-data-block-title">
                    {userIndex === 0
                      ? II18.get('create_case_your_data')
                      : II18.get('create_case_recipient_data', [
                          user.role
                            ? user.role
                            : II18.get('create_case_recipient', [userIndex + 1])
                        ])}
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  {user.needQes ? (
                    <div style={{ display: 'flex', maxWidth: 500 }}>
                      {getUserNameWidget(
                        'qesFirstName' + prefix,
                        userIndex,
                        qesFirstNameReadOnly,
                        II18.get(
                          userIndex > 0 || manyUsers
                            ? 'address_dialog_firstName'
                            : 'create_case_your_firstName'
                        ),
                        qesFirstName,
                        'qesFirstName'
                      )}
                      {getUserNameWidget(
                        'qesLastName' + prefix,
                        userIndex,
                        qesLastNameReadOnly,
                        II18.get(
                          userIndex > 0 || manyUsers
                            ? 'address_dialog_lastName'
                            : 'create_case_your_lastName'
                        ),
                        qesLastName,
                        'qesLastName'
                      )}
                    </div>
                  ) : (
                    getUserNameWidget(
                      'userName' + prefix,
                      userIndex,
                      nameReadOnly,
                      nameLabel,
                      userName,
                      'userName'
                    )
                  )}
                </div>

                <EmailPhoneInput
                  id={'userEmail' + prefix}
                  readonly={emailReadOnly}
                  label={emailPhoneLabel}
                  type="text"
                  placeholder={emailPhoneLabel}
                  value={userNumberOrEmail || ''}
                  onChange={v => updateUsersNumberOrEmail(v, userIndex)}
                />

                {user.needAes && (
                  <EmailPhoneInput
                    onlyPhone
                    region={region}
                    id={'userPhone' + prefix}
                    label={II18.get(
                      manyUsers
                        ? 'create_case_recipient_phone'
                        : 'create_case_phone'
                    )}
                    placeholder={II18.get(
                      manyUsers
                        ? 'create_case_recipient_phone'
                        : 'create_case_phone'
                    )}
                    value={userPhone || ''}
                    onChange={v => updateUsersPhone(v, userIndex)}
                  />
                )}
              </div>
            )
          );
        })}

        {userFields.map((field, index) => (
          <ListInput
            className={`user-field-${index} userFields`}
            key={field.id}
            id={field.id}
            outline
            floatingLabel
            required
            label={field.title}
            type="text"
            clearButton
            value={field.value || ''}
            onChange={e =>
              updateUsersData(
                {
                  userFields: arrUpdate(userFields, index, {
                    value: e.target.value
                  })
                },
                0
              )
            }
            onInputClear={e => {
              e.preventDefault();
              setTimeout(
                () =>
                  updateUsersData(
                    {
                      userFields: arrUpdate(userFields, index, {
                        value: ''
                      })
                    },
                    0
                  ),
                50
              );
            }}
          />
        ))}
      </List>
    </div>
  );
});

UserCredentialsFields.propTypes = {
  usersData: PropTypes.array,
  usersDataOk: PropTypes.func.isRequired,
  region: PropTypes.string,
  active: PropTypes.bool,
  onValidityChange: PropTypes.func,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object
};

export default UserCredentialsFields;
